import { type Draft } from 'immer';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import { type CanvasElementId, type ColumnId } from '@blaze-runtime/engine-domain';

import { sanitizeWrapperRefsMutably } from '../../../+state/elements/elements.reducer.utils';
import { isElementType } from '../../../canvas-elements/element-model/element-type-guards';
import { getWizardSteps } from '../../../elements/wizard/wizard.utils';
import { insertSlotMutably } from '../../../utils/layout/insert-column';
import { deleteSlotMutably } from '../../../utils/layout/remove-element';

/** Which slots a questionnaire regeneration added and dropped while matching steps. */
export interface QuestionnaireSlotsResult {
  /**
   * Slots dropped because no step claimed them, with the elements they held. Their ids are
   * immediately free again, so a caller keying anything by slot id must clear these before
   * reading the new ones — see {@link applyQuestionnaireSlotsMutably}.
   */
  removedSlotIds: ColumnId[];
  /** Slots minted for steps that had none, in step order. */
  addedSlotIds: ColumnId[];
  /**
   * The slots the wizard is left with, in step order — every id an editor's slot selection can
   * still point at once this pass is committed.
   */
  resultingSlotIds: ColumnId[];
}

/**
 * Makes a questionnaire wizard's slots match its steps: one slot per step, in step order.
 *
 * The steps are the desired state and the slots are derived from them, so this is idempotent —
 * a second call over the same wizard changes nothing. A step keeps its slot across reorders
 * (the slot travels with the step, it is not re-minted), which is what lets the question's
 * form, texts and connections survive a drag.
 *
 * **Slot ids are recycled.** `insertSlotMutably` mints the lowest free id, so a slot deleted
 * here can be handed straight back out to a new step in the same pass. Anything keyed by slot
 * id therefore has to be cleared for `removedSlotIds` before the new slots are keyed, or the
 * new question inherits the deleted one's references. Removal happens first here for exactly
 * that reason, and the wizard's wrapper refs are sanitized in between.
 *
 * @returns the slots added and removed, for the caller to reconcile whatever it keys by slot id
 */
export function applyQuestionnaireSlotsMutably(
  draft: Draft<EngineStateBase>,
  wizardId: CanvasElementId,
): QuestionnaireSlotsResult {
  const wizard = draft.entities[wizardId];
  if (!isElementType(wizard, 'wizard')) {
    throw new Error('element is not a wizard');
  }

  const steps = getWizardSteps(wizard.props);
  const claimedSlotIds = new Set(
    steps.map(({ slotId }) => slotId).filter((slotId): slotId is ColumnId => !!slotId),
  );

  // 1. Drop the slots no step claims anymore, taking their forms with them. Descending, so
  //    each index still addresses the slot it was computed for.
  const removedSlotIds: ColumnId[] = [];
  for (let index = wizard.slots.ids.length - 1; index >= 0; index--) {
    const slotId = wizard.slots.ids[index];
    if (claimedSlotIds.has(slotId)) {
      continue;
    }
    deleteSlotMutably(draft, wizardId, index);
    removedSlotIds.push(slotId);
  }
  if (removedSlotIds.length) {
    // deleteSlotMutably removed the slots' children, leaving wrapper refs pointing at nothing.
    sanitizeWrapperRefsMutably(draft, wizardId);
  }

  // 2. Mint a slot for every step that has none, or whose slot was just dropped as a leftover
  //    of an earlier state. Appending keeps the ids out of the way; step 3 puts them in order.
  const addedSlotIds: ColumnId[] = [];
  const liveSlotIds = new Set(wizard.slots.ids);
  for (const step of steps) {
    if (step.slotId && liveSlotIds.has(step.slotId)) {
      continue;
    }
    // why: insertSlotMutably inserts AFTER `index`, so the last index appends — and `-1` on an
    // empty wizard splices at 0, which is the same thing.
    const slotId = insertSlotMutably(draft, wizardId, wizard.slots.ids.length - 1);
    liveSlotIds.add(slotId);
    addedSlotIds.push(slotId);
    step.slotId = slotId;
  }

  // 3. Put the slots in step order. Assigning the whole list is the declarative form of the
  //    move-one-at-a-time reconcile it replaces; the slot records themselves are untouched.
  const resultingSlotIds = steps
    .map(({ slotId }) => slotId)
    .filter((slotId): slotId is ColumnId => !!slotId);
  wizard.slots.ids = resultingSlotIds;

  return { removedSlotIds, addedSlotIds, resultingSlotIds };
}
